import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { AiDialogHeader, TipUsageContent } from '@dev/ee-components';
import { ProductType } from '@dev/ee-api/ee-api-models';
import { Link } from '@material-ui/core';
import { Dialog as EEComponentsDialog, DialogBody, ElasticTheme } from '@dev/ee-components';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { useDispatch, useSelector } from 'react-redux';
import { Portal } from '@material-ui/core';
import { HideDialog } from '@/store/dialog/actions';
import { getAiTextStore } from '@/store/aiText/actions';
import { OpenUpsellingOverlay as OpenUpsellingOverlayLandingPageCreator } from '@/store/landingPageCreator/actions';
import { OpenUpsellingOverlay as OpenUpsellingOverlayTemplateEditor } from '@/store/templateEditor/actions';
import { OpenUpsellingOverlay as OpenUpsellingOverlayCheckoutCreator } from '@/store/checkoutCreator/actions';
import { getAccountOverview, getGeneralInfo } from '@/store/account/actions';
import { links } from '@/services/dictionaries/links';
import { useActivePlan } from '@/services/hooks/useActivePlan';
import { EditorType } from '@/services/dictionaries/EditorType';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
const RENEWS_FORMAT = 'MMM DD, YYYY';

export const AiTextModalHeader = (props: { editorType?: EditorType }): JSX.Element => {
    const dispatch = useDispatch();
    const { editorType } = props;
    const { IsPro, IsTrial, IsExpired, ProductType: productType } = useSelector(getGeneralInfo);
    const { AIToolsTextCount } = useSelector(getAccountOverview);
    const { ActivePlanDetails } = useActivePlan();
    const [isExitModalOpen, setIsExitModalOpen] = useState<boolean>(false);
    const { isBusy } = useSelector(getAiTextStore);
    const { isStandardPlan } = useCurrentPlanStatus();
    const isCreatorProduct = productType === ProductType.CreatorSuite;
    const isUpgradeable = isCreatorProduct ? !isStandardPlan : !IsPro;

    const { t } = useTranslation();
    const onClose = (): void => {
        if (isBusy) {
            setIsExitModalOpen(true);
            return;
        }
        close();
    };
    const onCancelClose = (): void => {
        setIsExitModalOpen(false);
    };
    const close = (): void => {
        dispatch(HideDialog());
    };

    const onUpgradeClick = (): void => {
        if (editorType === EditorType.LandingPageCreator) {
            dispatch(OpenUpsellingOverlayLandingPageCreator(AvailableFeaturesUpselling.AiTools));
            return;
        }
        if (editorType === EditorType.CheckoutCreator) {
            dispatch(OpenUpsellingOverlayCheckoutCreator(AvailableFeaturesUpselling.AiTools));
            return;
        }
        dispatch(OpenUpsellingOverlayTemplateEditor(AvailableFeaturesUpselling.AiTools));
    };

    const renewDate = ActivePlanDetails?.NextBillingDate
        ? dayjs(ActivePlanDetails?.NextBillingDate).format(RENEWS_FORMAT)
        : null;

    const upgradeInfo = [
        ...(!IsTrial && !IsExpired && renewDate
            ? [
                  {
                      text: (
                          <Trans
                              i18nKey="promptsusagerenew"
                              components={{ strong: <strong /> }}
                              values={{ date: renewDate ?? '' }}
                          />
                      ),
                  },
              ]
            : []),
        ...(isUpgradeable && !IsExpired
            ? [
                  {
                      text: t('wanttoincreaseyourlimit'),
                      linkText: t('upgradeformore'),
                      linkOnClick: !isBusy && onUpgradeClick,
                  },
              ]
            : []),
        ...(IsExpired
            ? [
                  {
                      text: (
                          <Trans
                              i18nKey="yourplanhasexpiredupgradeformore"
                              components={{ url: <Link onClick={onUpgradeClick} /> }}
                          />
                      ),
                  },
              ]
            : []),
        {
            text: (
                <Trans
                    i18nKey="learnmoreaboutouraitools"
                    components={{ url: <Link href={links.aiTextTool} target="_blank" /> }}
                />
            ),
        },
    ];
    const remainingCount =
        !IsExpired &&
        ActivePlanDetails.Limits?.AiToolsTextLimit &&
        ActivePlanDetails.Limits.AiToolsTextLimit - AIToolsTextCount >= 0
            ? ActivePlanDetails.Limits.AiToolsTextLimit - AIToolsTextCount
            : 0;
    return (
        <>
            <AiDialogHeader
                id={1}
                tipBody={
                    <TipUsageContent
                        title={t('promptusage')}
                        usageCount={IsExpired || !ActivePlanDetails.Limits?.AiToolsTextLimit ? null : AIToolsTextCount}
                        usageLimit={ActivePlanDetails.Limits?.AiToolsTextLimit}
                        usageRemaining={remainingCount}
                        id="promptsCount"
                        additionalInfo={upgradeInfo}
                    />
                }
                onCloseClick={onClose}
                count={remainingCount}
                title={t('aitexttool')}
                countLabel={t('prompts')}
            />
            <Portal>
                <EEComponentsDialog
                    BackdropProps={{ style: { backgroundColor: 'transparent' } }}
                    style={{ zIndex: ElasticTheme.zIndex.modal + 1 }}
                    open={isExitModalOpen}
                    title={t('aitexttool_exittitle')}
                    body={
                        <DialogBody variant="info" above={t('aitexttool_exitquestion')}>
                            {t('aitexttool_exitinfo')}
                        </DialogBody>
                    }
                    onCancelClick={onCancelClose}
                    onConfirmClick={close}
                    cancelLabel={t('cancel')}
                    confirmLabel={t('confirm')}
                    id="ai-text-tool-exit-modal"
                    width="598px"
                />
            </Portal>
        </>
    );
};
